import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { ManualBillItem } from '@/types';
import { useI18n } from '@/hooks/useI18n';
import ManualItemDialog from './ManualItemDialog';

interface ManualItemsListProps {
  manualItems: ManualBillItem[];
  onAddItem: (description: string, amount: number) => Promise<void>;
  onUpdateItem: (itemId: string, description: string, amount: number) => Promise<void>;
  onDeleteItem: (itemId: string) => Promise<void>;
  isLoading?: boolean;
}

const ManualItemsList: React.FC<ManualItemsListProps> = ({
  manualItems,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  isLoading = false,
}) => {
  const { t } = useI18n();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ManualBillItem | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);

  const handleAdd = async (description: string, amount: number) => {
    await onAddItem(description, amount);
    setIsAddDialogOpen(false);
  };

  const handleUpdate = async (description: string, amount: number) => {
    if (editingItem) {
      await onUpdateItem(editingItem.id, description, amount);
      setEditingItem(null);
    }
  };

  const handleDelete = async () => {
    if (deletingItemId) {
      await onDeleteItem(deletingItemId);
      setDeletingItemId(null);
    }
  };

  const totalManualAdjustment = manualItems.reduce((sum, item) => sum + item.amount, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2">
          {t('bill.persistent.manualItems')}
        </Typography>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => setIsAddDialogOpen(true)}
          disabled={isLoading}
        >
          {t('bill.persistent.addManualItem')}
        </Button>
      </Box>

      {manualItems.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          {t('bill.persistent.noManualItems')}
        </Typography>
      ) : (
        <>
          <List dense>
            {manualItems.map((item, index) => (
              <React.Fragment key={item.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={item.description}
                    secondary={
                      <>
                        {t('bill.persistent.manualItemCreatedBy')}: {item.createdByName || item.createdBy}
                        {' • '}
                        {new Date(item.createdAt).toLocaleDateString()}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        color={item.amount >= 0 ? 'text.primary' : 'error.main'}
                        sx={{ mr: 1 }}
                      >
                        {item.amount >= 0 ? '+' : ''}€{item.amount.toFixed(2)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setEditingItem(item)}
                        disabled={isLoading}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeletingItemId(item.id)}
                        disabled={isLoading}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>

          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              {t('bill.persistent.manualAdjustments')}:
            </Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
              color={totalManualAdjustment >= 0 ? 'success.main' : 'error.main'}
            >
              {totalManualAdjustment >= 0 ? '+' : ''}€{totalManualAdjustment.toFixed(2)}
            </Typography>
          </Box>
        </>
      )}

      {/* Add/Edit Dialog */}
      <ManualItemDialog
        open={isAddDialogOpen || !!editingItem}
        onClose={() => {
          setIsAddDialogOpen(false);
          setEditingItem(null);
        }}
        onConfirm={editingItem ? handleUpdate : handleAdd}
        manualItem={editingItem}
        isLoading={isLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deletingItemId}
        onClose={() => setDeletingItemId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('bill.persistent.deleteManualItem')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {t('bill.persistent.confirmDeleteManualItem')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingItemId(null)} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualItemsList;
